import { Routes, Route, Navigate } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { useState, useEffect } from 'react'
import { GET_CURRENT_USER } from './graphql/queries'
import { auth } from './lib/auth'
import Layout from './components/Layout'
import AuthGuard from './components/AuthGuard'
import { CartProvider } from './contexts/CartContext'
import { TerminalProvider } from './contexts/TerminalContext'
import { PaymentReopenProvider } from './contexts/PaymentReopenContext'
import { ReceivedOrdersProvider } from './contexts/ReceivedOrdersContext'
import LoginPage from './pages/LoginPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import AcceptInvitePage from './pages/AcceptInvitePage'
import ProfilePage from './pages/ProfilePage'
import DashboardPage from './pages/DashboardPage'
import ProductsPage from './pages/ProductsPage'
import ProductDetailPage from './pages/ProductDetailPage'
import CreateProductPage from './pages/CreateProductPage'
import EditProductPage from './pages/EditProductPage'
import AllergenOverviewPage from './pages/AllergenOverviewPage'
import CategoriesPage from './pages/CategoriesPage'
import CategoryDetailPage from './pages/CategoryDetailPage'
import EditCategoryPage from './pages/EditCategoryPage'
import CreateCategoryPage from './pages/CreateCategoryPage'
import OptionGroupsPage from './pages/OptionGroupsPage'
import OptionGroupDetailPage from './pages/OptionGroupDetailPage'
import CreateOptionGroupPage from './pages/CreateOptionGroupPage'
import EditOptionGroupPage from './pages/EditOptionGroupPage'
import OptionItemsPage from './pages/OptionItemsPage'
import OptionItemDetailPage from './pages/OptionItemDetailPage'
import CreateOptionItemPage from './pages/CreateOptionItemPage'
import EditOptionItemPage from './pages/EditOptionItemPage'
import VariantOptionGroupsPage from './pages/VariantOptionGroupsPage'
import CreateVariantOptionGroupPage from './pages/CreateVariantOptionGroupPage'
import EditVariantOptionGroupPage from './pages/EditVariantOptionGroupPage'
import ProductVariantDetailPage from './pages/ProductVariantDetailPage'
import CreateVariantPage from './pages/CreateVariantPage'
import EditVariantPage from './pages/EditVariantPage'
import UsersPage from './pages/UsersPage'
import RolesPage from './pages/RolesPage'
import CashRegistryPage from './pages/CashRegistryPage'
import RawMaterialsPage from './pages/costing/RawMaterialsPage'
import CreateRawMaterialPage from './pages/costing/CreateRawMaterialPage'
import RawMaterialDetailPage from './pages/costing/RawMaterialDetailPage'
import RecipesPage from './pages/costing/RecipesPage'
import CreateRecipePage from './pages/costing/CreateRecipePage'
import RecipeDetailPage from './pages/costing/RecipeDetailPage'
import VariantCostsPage from './pages/costing/VariantCostsPage'
import OptionItemCostsPage from './pages/costing/OptionItemCostsPage'
import BundleCostsPage from './pages/costing/BundleCostsPage'
import DiscountsPage from './pages/discounts/DiscountsPage'
import DiscountWizard from './pages/discounts/DiscountWizard'
import DiscountRuleDetailPage from './pages/discounts/DiscountRuleDetailPage'
import EditDiscountRulePage from './pages/discounts/EditDiscountRulePage'
import OrdersPage from './pages/OrdersPage'
import OrderHistoryPage from './pages/OrderHistoryPage'
import AccountingReportsPage from './pages/reports/AccountingReportsPage'
import DailyReportsPage from './pages/reports/DailyReportsPage'
import ProductReportsPage from './pages/reports/ProductReportsPage'
import DeliveryReportsPage from './pages/reports/DeliveryReportsPage'
import TimeSlotsPage from './pages/reports/TimeSlotsPage'
import EndOfShiftPage from './pages/end-of-shift/EndOfShiftPage'
import ExternalPlatformsPage from './pages/external-platforms/ExternalPlatformsPage'
import OrderDetailPage from './pages/OrderDetailPage'
import CustomerDisplayPage from './pages/CustomerDisplayPage'
import KitchenDisplayPage from './pages/KitchenDisplayPage'
import DeliveryBoardPage from './pages/DeliveryBoardPage'
import AnnouncementsFeedPage from './pages/announcements/AnnouncementsFeedPage'
import AnnouncementsAdminPage from './pages/announcements/AnnouncementsAdminPage'
import AnnouncementFormPage from './pages/announcements/AnnouncementFormPage'
import AnnouncementReceiptsPage from './pages/announcements/AnnouncementReceiptsPage'
import AnnouncementTypesPage from './pages/announcements/AnnouncementTypesPage'
import DeliverySettingsPage from './pages/DeliverySettingsPage'
import DriverPage from './pages/DriverPage'
import AvailabilityPage from './pages/AvailabilityPage'
import LoyaltyProgramsPage from './pages/loyalty/LoyaltyProgramsPage'
import LoyaltyProgramEditorPage from './pages/loyalty/LoyaltyProgramEditorPage'
import LoyaltyMembersPage from './pages/loyalty/LoyaltyMembersPage'
import HomeRedirect from './components/HomeRedirect'
import PermissionGuard from './components/PermissionGuard'
import { PERMISSIONS } from './lib/permissions'
// import PosOrdersPage from './pages/PosOrdersPage'


function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(auth.isAuthenticated())

  // Listen for authentication state changes
  useEffect(() => {
    const checkAuth = () => {
      const authenticated = auth.isAuthenticated()
      setIsAuthenticated(authenticated)
    }

    // Check auth state on mount
    checkAuth()

    // Listen for storage changes (when login/logout happens)
    const handleStorageChange = () => {
      checkAuth()
    }

    window.addEventListener('storage', handleStorageChange)
    
    // Also check periodically for immediate changes
    const interval = setInterval(checkAuth, 100)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      clearInterval(interval)
    }
  }, [])

  const { loading } = useQuery(GET_CURRENT_USER, {
    skip: !isAuthenticated,
    onError: (error) => {
      // Don't clear auth on query errors since we already have user data from login
      // Only clear if it's an authentication error
      if (error.graphQLErrors?.some(e => e.extensions?.code === 'UNAUTHENTICATED')) {
        auth.clearAuth()
        setIsAuthenticated(false)
      }
    }
  })



  // If not authenticated, show login page and public token-link pages
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  return (
    <CartProvider>
      <TerminalProvider>
        <PaymentReopenProvider>
          <Routes>
        {/* Public routes (also reachable with an active session, e.g. emailed links) */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />

        {/* Protected routes */}
        <Route path="/*" element={
          <AuthGuard loading={loading}>
            <ReceivedOrdersProvider>
            <Layout>
              <Routes>
              {/* HomeRedirect, not a bare Navigate: a DeliveryDriver role has no
                  dashboard permissions and lands on /driver instead. */}
              <Route path="/" element={<HomeRedirect />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/driver" element={
                <PermissionGuard permissions={[PERMISSIONS.DELIVERY_DRIVE]}>
                  <DriverPage />
                </PermissionGuard>
              } />
              {/* Reader flow (baseline) */}
              <Route path="/announcements" element={<AnnouncementsFeedPage />} />
              {/* Admin flow */}
              <Route path="/admin/announcements" element={
                <PermissionGuard permissions={[PERMISSIONS.MESSAGE_READ_OTHERS]} fallbackPath="/announcements">
                  <AnnouncementsAdminPage />
                </PermissionGuard>
              } />
              <Route path="/admin/announcements/new" element={
                <PermissionGuard permissions={[PERMISSIONS.MESSAGE_WRITE]} fallbackPath="/announcements">
                  <AnnouncementFormPage />
                </PermissionGuard>
              } />
              <Route path="/admin/announcements/types" element={
                <PermissionGuard permissions={[PERMISSIONS.MESSAGE_WRITE]} fallbackPath="/announcements">
                  <AnnouncementTypesPage />
                </PermissionGuard>
              } />
              <Route path="/admin/announcements/:id/edit" element={
                <PermissionGuard permissions={[PERMISSIONS.MESSAGE_WRITE]} fallbackPath="/announcements">
                  <AnnouncementFormPage />
                </PermissionGuard>
              } />
              <Route path="/admin/announcements/:id/receipts" element={
                <PermissionGuard permissions={[PERMISSIONS.MESSAGE_READ_RECEIPTS]} fallbackPath="/announcements">
                  <AnnouncementReceiptsPage />
                </PermissionGuard>
              } />
              <Route path="/admin/delivery" element={
                <PermissionGuard permissions={[PERMISSIONS.DELIVERY_VIEW]}>
                  <DeliverySettingsPage />
                </PermissionGuard>
              } />
              {/* Curry Me Club — the static list route sits above the /:id editor. */}
              <Route path="/loyalty" element={<Navigate to="/loyalty/programs" replace />} />
              <Route path="/loyalty/programs" element={
                <PermissionGuard permissions={[PERMISSIONS.LOYALTY_MANAGE]}>
                  <LoyaltyProgramsPage />
                </PermissionGuard>
              } />
              <Route path="/loyalty/programs/:id" element={
                <PermissionGuard permissions={[PERMISSIONS.LOYALTY_MANAGE]}>
                  <LoyaltyProgramEditorPage />
                </PermissionGuard>
              } />
              <Route path="/loyalty/members" element={
                <PermissionGuard permissions={[PERMISSIONS.LOYALTY_MANAGE]}>
                  <LoyaltyMembersPage />
                </PermissionGuard>
              } />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/products/new" element={<CreateProductPage />} />
              <Route path="/products/:id" element={<ProductDetailPage />} />
              <Route path="/products/:id/edit" element={<EditProductPage />} />
              <Route path="/allergens" element={<AllergenOverviewPage />} />
              <Route path="/categories" element={<CategoriesPage />} />
              <Route path="/categories/new" element={<CreateCategoryPage />} />
              <Route path="/categories/:id" element={<CategoryDetailPage />} />
              <Route path="/categories/:id/edit" element={<EditCategoryPage />} />
              <Route path="/option-groups" element={<OptionGroupsPage />} />
              <Route path="/option-groups/new" element={<CreateOptionGroupPage />} />
              <Route path="/option-groups/:id" element={<OptionGroupDetailPage />} />
              <Route path="/option-groups/:id/edit" element={<EditOptionGroupPage />} />
                      <Route path="/option-items" element={<OptionItemsPage />} />
        <Route path="/option-items/new" element={<CreateOptionItemPage />} />
        <Route path="/option-items/:id" element={<OptionItemDetailPage />} />
        <Route path="/option-items/:id/edit" element={<EditOptionItemPage />} />
        <Route path="/variant-option-groups" element={<VariantOptionGroupsPage />} />
        <Route path="/variant-option-groups/new" element={<CreateVariantOptionGroupPage />} />
        <Route path="/variant-option-groups/:id/edit" element={<EditVariantOptionGroupPage />} />
        <Route path="/products/:id/variants/new" element={<CreateVariantPage />} />
        <Route path="/products/:productId/variants/:variantId" element={<ProductVariantDetailPage />} />
        <Route path="/products/:productId/variants/:variantId/edit" element={<EditVariantPage />} />
              <Route path="/cash-registry" element={<CashRegistryPage />} />
              <Route path="/pos" element={<Navigate to="/cash-registry" replace />} />
              <Route path="/orders" element={<OrdersPage />} />
              {/* Old bookmark-friendly entry point for the reports group */}
              <Route path="/reports" element={<Navigate to="/reports/accounting" replace />} />
              <Route path="/reports/accounting" element={<AccountingReportsPage />} />
              <Route path="/reports/daily" element={<DailyReportsPage />} />
              <Route path="/reports/products" element={<ProductReportsPage />} />
              <Route path="/reports/delivery" element={<DeliveryReportsPage />} />
              <Route path="/reports/time-slots" element={<TimeSlotsPage />} />
              <Route path="/orders/display" element={<CustomerDisplayPage />} />
              <Route path="/orders/kitchen" element={<KitchenDisplayPage />} />
              {/* Static routes — must stay above /orders/:id */}
              <Route path="/orders/history" element={
                <PermissionGuard permissions={[PERMISSIONS.ORDER_LIST_ALL]} fallbackPath="/orders">
                  <OrderHistoryPage />
                </PermissionGuard>
              } />
              <Route path="/orders/delivery" element={
                <PermissionGuard
                  permissions={[PERMISSIONS.ORDER_LIST_TODAY, PERMISSIONS.ORDER_LIST_7DAYS, PERMISSIONS.ORDER_LIST_ALL]}
                  fallbackPath="/orders"
                >
                  <DeliveryBoardPage />
                </PermissionGuard>
              } />
              <Route path="/orders/:id" element={<OrderDetailPage />} />
              <Route path="/costing/raw-materials" element={<RawMaterialsPage />} />
              <Route path="/costing/raw-materials/new" element={<CreateRawMaterialPage />} />
              <Route path="/costing/raw-materials/:id" element={<RawMaterialDetailPage />} />
              <Route path="/costing/recipes" element={<RecipesPage />} />
              <Route path="/costing/recipes/new" element={<CreateRecipePage />} />
              <Route path="/costing/recipes/:id" element={<RecipeDetailPage />} />
              <Route path="/costing/variants" element={<VariantCostsPage />} />
              <Route path="/costing/option-item-costs" element={<OptionItemCostsPage />} />
              <Route path="/costing/bundles" element={<BundleCostsPage />} />
              <Route path="/discounts" element={<Navigate to="/discounts/automatic" replace />} />
              <Route path="/discounts/automatic" element={<DiscountsPage triggerFilter="AUTOMATIC" />} />
              <Route path="/discounts/coupon" element={<DiscountsPage triggerFilter="CODE" />} />
              <Route path="/discounts/staff" element={<DiscountsPage triggerFilter="STAFF_APPLIED" />} />
              <Route path="/discounts/new/:type" element={<DiscountWizard />} />
              <Route path="/discounts/:id" element={<DiscountRuleDetailPage />} />
              <Route path="/discounts/:id/edit" element={<EditDiscountRulePage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/roles" element={<RolesPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/end-of-shift" element={<EndOfShiftPage />} />
              <Route path="/availability" element={
                <PermissionGuard permissions={[PERMISSIONS.PRODUCT_AVAILABILITY]}>
                  <AvailabilityPage />
                </PermissionGuard>
              } />
              <Route path="/external-platforms" element={<ExternalPlatformsPage />} />
              <Route path="*" element={<HomeRedirect />} />
                </Routes>
              </Layout>
            </ReceivedOrdersProvider>
          </AuthGuard>
        } />
          </Routes>
        </PaymentReopenProvider>
      </TerminalProvider>
    </CartProvider>
  )
}

export default App
